import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  BadgeCheck, Ban, FlaskConical, Link2, Link2Off, Plus,
  RefreshCw, ShieldAlert, ShieldCheck,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLAIM_TYPES = [
  "general_fact", "service_or_category", "opening_hours", "contact_route",
  "returns_policy", "price", "delivery", "compatibility",
  "technical_specification", "warranty", "promotion",
];

const STATUS_STYLE = {
  draft: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  revoked: "bg-red-50 text-red-600 border-red-200",
};

const EMPTY_FORM = {
  fact_id: "", predicate: "", subject_label: "", subject_id: "",
  subject_type: "company", value_type: "string", value_text: "",
  amount: "", currency: "SEK",
  entity_ids: "", target_ids: "",
  source_excerpt: "", retrieved_at: "",
  claim_types: ["general_fact"], source_type: "admin_attestation",
  source_url: "", confidence: "medium", freshness_policy: "stable",
  ttl_seconds: "",
};

const splitCsv = (s) =>
  s.split(",").map((x) => x.trim()).filter(Boolean);

function FactForm({ onCreated, clientId, getAuthHeaders }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      const entityIds = splitCsv(form.entity_ids);
      const targetIds = splitCsv(form.target_ids);
      const body = {
        fact_id: form.fact_id.trim(),
        predicate: form.predicate.trim(),
        subject: { type: form.subject_type, id: form.subject_id.trim() || clientId,
                   label: form.subject_label.trim() || form.fact_id },
        value: form.value_type === "money"
          ? { type: "money", amount: Number(form.amount),
              currency: form.currency.trim() || "SEK" }
          : { type: "string", text: form.value_text.trim() },
        allowed_claim_types: form.claim_types,
        source_type: form.source_type,
        source_url: form.source_url.trim() || undefined,
        source_excerpt: form.source_excerpt.trim() || undefined,
        retrieved_at: form.retrieved_at
          ? new Date(form.retrieved_at).toISOString() : undefined,
        applicable_scope: (entityIds.length || targetIds.length)
          ? { entity_ids: entityIds, target_ids: targetIds,
              required_context_keys: [] }
          : undefined,
        confidence: form.confidence,
        freshness_policy: form.freshness_policy,
        ttl_seconds: form.freshness_policy === "ttl"
          ? Number(form.ttl_seconds) || 86400 : undefined,
      };
      const res = await fetch(
        `${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/evidence`,
        { method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify(body) },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(typeof err.detail === "string" ? err.detail : "Kunde inte skapa fakta");
      }
      toast.success("Utkast skapat — granska och godkänn innan det kan användas.");
      setForm(EMPTY_FORM);
      onCreated();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3"
         data-testid="evidence-create-form">
      <p className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
        <Plus className="w-3.5 h-3.5" /> Nytt faktautkast (kräver godkännande + länkning innan runtime)
      </p>
      <div className="grid grid-cols-2 gap-2">
        <input className="border rounded px-2 py-1.5 text-xs" placeholder="fact_id (snake_case)"
               data-testid="evidence-fact-id-input"
               value={form.fact_id} onChange={(e) => set("fact_id", e.target.value)} />
        <input className="border rounded px-2 py-1.5 text-xs" placeholder="predicate (t.ex. opening_hours)"
               value={form.predicate} onChange={(e) => set("predicate", e.target.value)} />
        <div className="flex gap-1.5">
          <select className="border rounded px-2 py-1 text-xs"
                  data-testid="evidence-subject-type"
                  value={form.subject_type} onChange={(e) => set("subject_type", e.target.value)}>
            <option value="company">company</option>
            <option value="product">product</option>
            <option value="service">service</option>
          </select>
          <input className="border rounded px-2 py-1.5 text-xs flex-1" placeholder="Ämnesetikett (t.ex. Butiken)"
                 value={form.subject_label} onChange={(e) => set("subject_label", e.target.value)} />
        </div>
        <input className="border rounded px-2 py-1.5 text-xs" placeholder="Ämnes-id (t.ex. SKU)"
               data-testid="evidence-subject-id-input"
               value={form.subject_id} onChange={(e) => set("subject_id", e.target.value)} />
        <input className="border rounded px-2 py-1.5 text-xs" placeholder="Käll-URL (https://…)"
               data-testid="evidence-source-url-input"
               value={form.source_url} onChange={(e) => set("source_url", e.target.value)} />
        <input className="border rounded px-2 py-1.5 text-xs" type="datetime-local"
               title="Hämtad (retrieved_at)" data-testid="evidence-retrieved-at-input"
               value={form.retrieved_at} onChange={(e) => set("retrieved_at", e.target.value)} />
      </div>
      <div className="flex gap-1.5 items-start">
        <select className="border rounded px-2 py-1 text-xs"
                data-testid="evidence-value-type"
                value={form.value_type} onChange={(e) => set("value_type", e.target.value)}>
          <option value="string">text</option>
          <option value="money">belopp (money)</option>
        </select>
        {form.value_type === "money" ? (
          <>
            <input className="border rounded px-2 py-1.5 text-xs w-28" placeholder="Belopp"
                   data-testid="evidence-amount-input"
                   value={form.amount} onChange={(e) => set("amount", e.target.value)} />
            <input className="border rounded px-2 py-1.5 text-xs w-20" placeholder="Valuta"
                   value={form.currency} onChange={(e) => set("currency", e.target.value)} />
          </>
        ) : (
          <textarea className="border rounded px-2 py-1.5 text-xs flex-1" rows={2}
                    placeholder="Faktavärde (text)" data-testid="evidence-value-input"
                    value={form.value_text} onChange={(e) => set("value_text", e.target.value)} />
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input className="border rounded px-2 py-1.5 text-xs"
               placeholder="Scope entity_ids (komma-separerade, t.ex. blad-x)"
               data-testid="evidence-entity-ids-input"
               value={form.entity_ids} onChange={(e) => set("entity_ids", e.target.value)} />
        <input className="border rounded px-2 py-1.5 text-xs"
               placeholder="Scope target_ids (t.ex. husqvarna-435)"
               data-testid="evidence-target-ids-input"
               value={form.target_ids} onChange={(e) => set("target_ids", e.target.value)} />
      </div>
      <textarea className="border rounded px-2 py-1.5 text-xs w-full" rows={2}
                placeholder="Källutdrag (source_excerpt — endast för granskning, skickas aldrig till LLM/widget)"
                data-testid="evidence-source-excerpt-input"
                value={form.source_excerpt} onChange={(e) => set("source_excerpt", e.target.value)} />
      <div className="flex flex-wrap gap-1.5">
        {CLAIM_TYPES.map((ct) => (
          <button key={ct} type="button"
                  data-testid={`evidence-claimtype-${ct}`}
                  onClick={() => set("claim_types",
                    form.claim_types.includes(ct)
                      ? form.claim_types.filter((x) => x !== ct)
                      : [...form.claim_types, ct])}
                  className={`text-[10px] px-2 py-0.5 rounded-full border ${
                    form.claim_types.includes(ct)
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-gray-500 border-gray-300"}`}>
            {ct}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2 text-xs">
        <select className="border rounded px-2 py-1" value={form.source_type}
                onChange={(e) => set("source_type", e.target.value)}>
          <option value="admin_attestation">admin_attestation</option>
          <option value="scrape">scrape</option>
          <option value="document">document</option>
          <option value="feed">feed</option>
        </select>
        <select className="border rounded px-2 py-1" value={form.confidence}
                onChange={(e) => set("confidence", e.target.value)}>
          <option value="high">hög tillförlitlighet</option>
          <option value="medium">medium</option>
          <option value="low">låg (endast caveat)</option>
        </select>
        <select className="border rounded px-2 py-1" value={form.freshness_policy}
                onChange={(e) => set("freshness_policy", e.target.value)}>
          <option value="stable">stabil</option>
          <option value="ttl">TTL (färskvara)</option>
        </select>
        {form.freshness_policy === "ttl" && (
          <input className="border rounded px-2 py-1 w-24" placeholder="TTL sek"
                 value={form.ttl_seconds}
                 onChange={(e) => set("ttl_seconds", e.target.value)} />
        )}
        <button type="button" disabled={saving || !form.fact_id ||
                  (form.value_type === "money" ? !form.amount : !form.value_text)}
                data-testid="evidence-create-submit"
                onClick={submit}
                className="ml-auto px-3 py-1.5 rounded bg-emerald-600 text-white text-xs font-semibold disabled:opacity-40">
          Skapa utkast
        </button>
      </div>
    </div>
  );
}

function ReceiptPreview({ clientId, getAuthHeaders }) {
  const [msg, setMsg] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [running, setRunning] = useState(false);
  const [usePublished, setUsePublished] = useState(false);

  const run = async () => {
    setRunning(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/evidence/answerability-preview`,
        { method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify({ message: msg, use_published: usePublished }) },
      );
      if (!res.ok) throw new Error("Preview misslyckades");
      setReceipt(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-2"
         data-testid="evidence-receipt-preview">
      <p className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
        <FlaskConical className="w-3.5 h-3.5" /> Answerability-preview (ingen LLM, inga skrivningar)
      </p>
      <div className="flex gap-2 items-center">
        <input className="border rounded px-2 py-1.5 text-xs flex-1"
               placeholder="Testfråga, t.ex. 'Vad kostar servicen?'"
               data-testid="evidence-preview-input"
               value={msg} onChange={(e) => setMsg(e.target.value)} />
        <label className="flex items-center gap-1 text-[10px] text-gray-600 whitespace-nowrap"
               title="Standard: senaste editerbara version. Publicerad version är ett explicit val.">
          <input type="checkbox" checked={usePublished}
                 data-testid="evidence-preview-use-published"
                 onChange={(e) => setUsePublished(e.target.checked)} />
          publicerad version
        </label>
        <button type="button" disabled={running || !msg.trim()} onClick={run}
                data-testid="evidence-preview-run"
                className="px-3 py-1.5 rounded bg-gray-900 text-white text-xs font-semibold disabled:opacity-40">
          Kör
        </button>
      </div>
      {receipt && (
        <pre className="text-[10px] bg-gray-50 border rounded p-2 overflow-auto max-h-72"
             data-testid="evidence-receipt-json">
          {JSON.stringify(receipt.answer_receipt, null, 2)}
        </pre>
      )}
    </div>
  );
}

export const BlueprintEvidencePanel = ({ clientId, getAuthHeaders }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/evidence`,
        { credentials: "include", headers: getAuthHeaders() },
      );
      if (!res.ok) throw new Error("Kunde inte hämta fakta");
      setData(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const act = async (path, body, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      const res = await fetch(
        `${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/evidence${path}`,
        { method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify(body || {}) },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(typeof err.detail === "string" ? err.detail : "Åtgärden misslyckades");
      }
      await load();
      toast.success("Klart");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const setMode = async (mode) => {
    try {
      const res = await fetch(
        `${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/evidence/knowledge-mode`,
        { method: "PUT", credentials: "include",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify({ knowledge_mode: mode }) },
      );
      if (!res.ok) throw new Error("Kunde inte byta läge");
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading && !data) {
    return <p className="text-xs text-gray-400 p-4">Laddar verifierade fakta…</p>;
  }
  const facts = data?.facts || [];
  const linked = new Set(data?.linked_fact_refs || []);
  const mode = data?.knowledge_mode || "guidance_only";

  return (
    <div className="space-y-4" data-testid="blueprint-evidence-panel">
      <div className={`rounded-lg border p-3 flex items-center gap-2 text-xs ${
        mode === "verified"
          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : "border-amber-200 bg-amber-50 text-amber-800"}`}
           data-testid="evidence-knowledge-mode-banner">
        {mode === "verified"
          ? <ShieldCheck className="w-4 h-4" />
          : <ShieldAlert className="w-4 h-4" />}
        <span className="font-semibold">
          Kunskapsläge: {mode === "verified" ? "Verified" : "Guidance only"}
        </span>
        <span className="text-[11px] opacity-80">
          {mode === "verified"
            ? "Publicering kräver minst en användbar verifierad fakta."
            : "VARNING: agenten svarar utan verifierad evidens (högriskpåståenden blockeras alltid)."}
        </span>
        <button type="button"
                data-testid="evidence-toggle-mode"
                onClick={() => setMode(mode === "verified" ? "guidance_only" : "verified")}
                className="ml-auto px-2.5 py-1 rounded border bg-white text-gray-700 font-semibold">
          Byt till {mode === "verified" ? "guidance_only" : "verified"}
        </button>
      </div>

      <FactForm clientId={clientId} getAuthHeaders={getAuthHeaders} onCreated={load} />

      <div className="rounded-lg border border-gray-200 bg-white divide-y"
           data-testid="evidence-facts-list">
        <div className="px-3 py-2 flex items-center justify-between">
          <p className="text-xs font-semibold text-gray-700">
            Fakta ({facts.length}) — länkade: {linked.size}
          </p>
          <button type="button" onClick={load} data-testid="evidence-refresh"
                  className="text-gray-400 hover:text-gray-700">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
        {facts.length === 0 && (
          <p className="px-3 py-4 text-xs text-gray-400">
            Inga fakta ännu. Skapa ett utkast ovan — inget godkänns eller länkas automatiskt.
          </p>
        )}
        {facts.map((f) => (
          <div key={f.fact_ref} className="px-3 py-2 flex items-start gap-2"
               data-testid={`evidence-fact-${f.fact_ref}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs font-mono font-semibold text-gray-800">{f.fact_ref}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_STYLE[f.status] || ""}`}>
                  {f.status}
                </span>
                {f.freshness_state === "expired" && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded border bg-red-50 text-red-600 border-red-200">
                    utgången
                  </span>
                )}
                {linked.has(f.fact_ref) && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded border bg-blue-50 text-blue-600 border-blue-200">
                    länkad
                  </span>
                )}
              </div>
              <p className="text-[11px] text-gray-600 truncate">
                {(f.subject?.label || "")} — {f.predicate}: {f.value?.text || f.value?.amount || ""}
              </p>
              <p className="text-[10px] text-gray-400">
                {(f.allowed_claim_types || []).join(", ")} · {f.source_type}
                {f.verified_at ? ` · verifierad ${String(f.verified_at).slice(0, 10)} av ${f.verified_by}` : ""}
              </p>
              {f.status === "draft" && (f.approval_blockers || []).length > 0 && (
                <div className="mt-1 text-[10px] text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-1"
                     data-testid={`evidence-blockers-${f.fact_ref}`}>
                  <span className="font-semibold">Blockerare före godkännande:</span>
                  <ul className="list-disc ml-4">
                    {f.approval_blockers.map((b) => <li key={b}>{b}</li>)}
                  </ul>
                </div>
              )}
            </div>
            <div className="flex items-center gap-1">
              {f.status === "draft" && (
                <button type="button"
                        title={(f.approval_blockers || []).length
                          ? `Blockerad: ${f.approval_blockers.join("; ")}` : "Godkänn"}
                        disabled={(f.approval_blockers || []).length > 0}
                        data-testid={`evidence-approve-${f.fact_ref}`}
                        onClick={() => act(`/${encodeURIComponent(f.fact_ref)}/approve`,
                          { confirm: true },
                          `Godkänn ${f.fact_ref}? Fakta blir oföränderlig.`)}
                        className="p-1.5 rounded border text-emerald-600 border-emerald-200 hover:bg-emerald-50 disabled:opacity-30 disabled:cursor-not-allowed">
                  <BadgeCheck className="w-3.5 h-3.5" />
                </button>
              )}
              {f.status === "approved" && !linked.has(f.fact_ref) && (
                <button type="button" title="Länka till blueprint"
                        data-testid={`evidence-link-${f.fact_ref}`}
                        onClick={() => act("/link", { fact_ref: f.fact_ref })}
                        className="p-1.5 rounded border text-blue-600 border-blue-200 hover:bg-blue-50">
                  <Link2 className="w-3.5 h-3.5" />
                </button>
              )}
              {linked.has(f.fact_ref) && (
                <button type="button" title="Avlänka"
                        data-testid={`evidence-unlink-${f.fact_ref}`}
                        onClick={() => act("/unlink", { fact_ref: f.fact_ref })}
                        className="p-1.5 rounded border text-gray-500 border-gray-200 hover:bg-gray-50">
                  <Link2Off className="w-3.5 h-3.5" />
                </button>
              )}
              {f.status === "approved" && (
                <button type="button" title="Återkalla"
                        data-testid={`evidence-revoke-${f.fact_ref}`}
                        onClick={() => act(`/${encodeURIComponent(f.fact_ref)}/revoke`,
                          { reason: "Återkallad av admin" },
                          `Återkalla ${f.fact_ref}? Slutar användas direkt i runtime.`)}
                        className="p-1.5 rounded border text-red-500 border-red-200 hover:bg-red-50">
                  <Ban className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <ReceiptPreview clientId={clientId} getAuthHeaders={getAuthHeaders} />
    </div>
  );
};

export default BlueprintEvidencePanel;
